/**
 * P-12 新建授权页（/licenses/new）。
 *
 * 管理员手动创建授权，**不消耗库存码**（区别于 P-09 批次兑换）。
 * 用于补发、测试、赠送；后端返回 License（含 plan_snapshot）并记审计。
 *
 * 字段契约：openapi CreateLicenseRequest —
 * - product_id / plan_id 必填（且 plan 必须属于该产品）
 * - customer_id 可选（不关联则 null）
 * - expires_at 为 null 表示永久授权
 * - note 可选，≤ 1000 字符
 *
 * 校验全部在客户端挡一层（与服务端 zod 同口径），省去无谓的 400 往返；
 * 真实后端的语义错误（如产品/套餐不存在）由 createLicense 抛 ApiError 透出。
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '../../icons/Icon';
import { ICON_SIZE } from '../../tokens';
import { ErrorBlock, CardSkeleton } from '../../components/DataState';
import { useAsync } from '../../lib/useAsync';
import { describeError } from '../../api/client';
import {
  createLicense,
  listCustomers,
  listPlans,
  listProducts,
} from '../../api/endpoints';
import type { Customer, CreateLicenseRequest, License, Plan, Product } from '../../api/types';

const NOTE_MAX = 1000;

export function NewLicensePage() {
  const navigate = useNavigate();

  const products = useAsync(() => listProducts(), []);
  const customers = useAsync(() => listCustomers(), []);

  const [productId, setProductId] = useState('');
  const [planId, setPlanId] = useState('');
  const [customerId, setCustomerId] = useState(''); // '' = 不关联客户
  const [orderId, setOrderId] = useState(''); // '' = 不关联订单
  const [perpetual, setPerpetual] = useState(false);
  const [dateStr, setDateStr] = useState(''); // YYYY-MM-DD；perpetual 或空串 → 永久
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<License | null>(null);

  const productList: readonly Product[] =
    products.state.kind === 'ready' ? products.state.data.items : [];

  // 套餐列表依赖所选产品：产品未定时不发起请求
  const plans = useAsync(
    useCallback(() => (productId ? listPlans(productId) : Promise.resolve({ items: [] as Plan[] })), [productId]),
    [productId],
  );
  const planList: readonly Plan[] = plans.state.kind === 'ready' ? plans.state.data.items : [];

  const customerList: readonly Customer[] =
    customers.state.kind === 'ready' ? customers.state.data.items : [];

  // 切换产品：套餐与到期默认值一并复位，避免脏状态
  const onProductChange = (pid: string) => {
    setProductId(pid);
    setPlanId('');
    setPerpetual(false);
    setDateStr('');
  };

  const onPlanChange = (pid: string) => {
    setPlanId(pid);
    const plan = planList.find((p) => p.id === pid);
    if (plan && plan.duration_days === null) {
      // 该套餐本身无期限 → 默认勾选永久，隐藏日期
      setPerpetual(true);
      setDateStr('');
    } else {
      setPerpetual(false);
      // 按套餐时长预填到期日（无时长兜底 365 天），减少运营手填成本
      const days = plan?.duration_days ?? 365;
      setDateStr(addDaysToDateInput(days));
    }
  };

  const productReady = products.state.kind === 'ready';
  const planReady = productId === '' ? true : plans.state.kind === 'ready';

  // 来源订单号（可选）：非空必须是 UUID（后端 zod 严格校验，否则 1002）
  const UUID_RE = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
  const orderIdValid = orderId.trim() === '' || UUID_RE.test(orderId.trim());

  // 校验：产品 + 套餐必填；非永久则必须给出到期日；订单号若填写必须是 UUID
  const formValid = productId !== '' && planId !== '' && (perpetual || dateStr !== '') && orderIdValid;

  const expiresAt = useMemo<number | null>(() => {
    if (perpetual || dateStr === '') return null;
    return fromDateInput(dateStr);
  }, [perpetual, dateStr]);

  const handleSubmit = async () => {
    if (submitting || !formValid) return;
    setError(null);
    setSubmitting(true);
    try {
      const body: CreateLicenseRequest = {
        product_id: productId,
        plan_id: planId,
        customer_id: customerId === '' ? null : customerId,
        order_id: orderId.trim() === '' ? null : orderId.trim(),
        expires_at: expiresAt,
        note: note.trim() === '' ? null : note.trim().slice(0, NOTE_MAX),
      };
      const lic = await createLicense(body);
      setCreated(lic);
      navigate(`/licenses/${lic.id}`, { replace: true });
    } catch (e) {
      setError(describeError(e, '创建授权'));
      setSubmitting(false);
    }
  };

  // 创建成功后等待跳转完成前的瞬间，仍渲染当前页（navigate 通常已同步切换路由）
  useEffect(() => {
    if (created) navigate(`/licenses/${created.id}`, { replace: true });
  }, [created, navigate]);

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1 className="page-head__title">新建授权</h1>
          <p className="text-muted" style={{ marginTop: 4 }}>
            手动创建一条正式授权，不消耗库存码。适用于补发、测试或赠送场景。
          </p>
        </div>
        <div className="page-head__actions">
          <button type="button" className="btn btn--quiet" onClick={() => navigate('/licenses')}>
            返回授权列表
          </button>
        </div>
      </div>

      <div className="card" style={{ maxWidth: 640 }}>
        {/* 产品加载中：骨架占位 */}
        {products.state.kind === 'loading' && <CardSkeleton lines={4} />}
        {products.state.kind === 'failed' && (
          <ErrorBlock message="产品列表加载失败" onRetry={products.reload} />
        )}

        {products.state.kind === 'ready' && productList.length === 0 && (
          <p className="text-muted">尚无任何产品，请先在「产品」中创建产品再新建授权。</p>
        )}

        {productReady && productList.length > 0 && (
          <>
            <div className="field">
              <label>产品 <span className="req">*</span></label>
              <select className="select" value={productId} onChange={(e) => onProductChange(e.target.value)}>
                <option value="">请选择产品</option>
                {productList.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
              <p className="text-muted" style={{ fontSize: 12 }}>授权归属的产品，决定其能力边界与签名密钥。</p>
            </div>

            <div className="field">
              <label>套餐 <span className="req">*</span></label>
              {plans.state.kind === 'loading' ? (
                <CardSkeleton lines={1} />
              ) : plans.state.kind === 'failed' ? (
                <ErrorBlock message="套餐列表加载失败" onRetry={plans.reload} />
              ) : (
                <select
                  className="select"
                  value={planId}
                  disabled={productId === ''}
                  onChange={(e) => onPlanChange(e.target.value)}
                >
                  <option value="">{productId === '' ? '请先选择产品' : '请选择套餐'}</option>
                  {planList.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}（{p.duration_days === null ? '永久' : `${p.duration_days} 天`} · 最多 {p.max_devices} 设备）
                    </option>
                  ))}
                </select>
              )}
              <p className="text-muted" style={{ fontSize: 12 }}>套餐的权益与设备上限将作为快照写入授权，不受后续套餐变更影响。</p>
            </div>

            <div className="field">
              <label>关联客户</label>
              <select className="select" value={customerId} onChange={(e) => setCustomerId(e.target.value)}>
                <option value="">不关联客户</option>
                {customerList.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name ?? c.email ?? c.id}
                  </option>
                ))}
              </select>
            </div>

            <div className="field">
              <label>来源订单号（可选，UUID）</label>
              <input
                className={`input ${!orderIdValid ? 'input--warn' : ''}`}
                type="text"
                value={orderId}
                onChange={(e) => setOrderId(e.target.value)}
                placeholder="019f…-4a81-7b29-b58e-7dec93849f15"
                aria-invalid={!orderIdValid}
              />
              {!orderIdValid ? (
                <p className="field-hint">格式不对：订单号需为 UUID（可在「客户与订单」页订单行复制）。</p>
              ) : (
                <p className="text-muted" style={{ fontSize: 12 }}>关联一笔 CRM 订单，留空则不关联。</p>
              )}
            </div>

            <div className="field">
              <label className="license-toggle license-toggle--block">
                <input
                  type="checkbox"
                  checked={perpetual}
                  onChange={(e) => {
                    setPerpetual(e.target.checked);
                    if (e.target.checked) setDateStr('');
                  }}
                />
                永久授权（无到期时间）
              </label>
              {!perpetual && (
                <div className="field" style={{ marginTop: 8 }}>
                  <label>到期日期</label>
                  <input
                    className={`input ${dateStr === '' ? 'input--warn' : ''}`}
                    type="date"
                    value={dateStr}
                    onChange={(e) => setDateStr(e.target.value)}
                  />
                  <p className="text-muted" style={{ fontSize: 12 }}>留空则视为永久授权。到期日按 UTC 当天 00:00 计算。</p>
                </div>
              )}
            </div>

            <div className="field">
              <label>备注</label>
              <textarea
                className="input"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={3}
                maxLength={NOTE_MAX}
                placeholder="可选，写入审计记录（≤ 1000 字符）"
              />
              <p className="text-muted" style={{ fontSize: 12 }}>{note.length}/{NOTE_MAX}</p>
            </div>

            {error && (
              <p className="login-error">
                <Icon name="octagon-x" size={ICON_SIZE.inline} />
                {error}
              </p>
            )}

            <div className="dialog-actions" style={{ marginTop: 16 }}>
              <button type="button" className="btn btn--quiet" onClick={() => navigate('/licenses')}>
                取消
              </button>
              <button
                type="button"
                className="btn btn--primary"
                disabled={submitting || !formValid || !planReady}
                onClick={handleSubmit}
              >
                <Icon name="plus" size={ICON_SIZE.control} />
                {submitting ? '创建中…' : '创建授权'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/* ── 日期输入转换（本地时区，epoch 秒），与 LicenseDetailPage 同口径 ── */

function fromDateInput(value: string): number {
  return Math.floor(new Date(`${value}T00:00:00`).getTime() / 1000);
}

function addDaysToDateInput(days: number): string {
  const base = new Date();
  base.setDate(base.getDate() + days);
  const y = base.getFullYear();
  const m = String(base.getMonth() + 1).padStart(2, '0');
  const day = String(base.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
